import { useState, useEffect } from 'react'
import {
    Calculator,
    Search,
    DollarSign,
    Calendar,
    Building,
    Loader2,
    AlertCircle,
    TrendingUp,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { payoutService } from '@/services/payoutService'
import { CommissionRecord } from '@/types'
import { safeFormatDistanceToNow } from '@/utils/date'

export default function AdminCommissionRecordsList() {
    const [records, setRecords] = useState<CommissionRecord[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [merchantFilter, setMerchantFilter] = useState<string>('')
    const [currencyFilter, setCurrencyFilter] = useState<string>('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [pageSize] = useState(20)
    const [totalCommission, setTotalCommission] = useState(0)

    useEffect(() => {
        fetchCommissionRecords()
    }, [currentPage, merchantFilter, currencyFilter])

    const fetchCommissionRecords = async () => {
        try {
            setLoading(true)
            setError(null)

            const params = {
                page: currentPage,
                page_size: pageSize,
                ...(merchantFilter && { merchant: parseInt(merchantFilter) }),
                ...(currencyFilter && { currency: currencyFilter }),
            }

            const response = await payoutService.getCommissionRecords(params)

            if (response.success) {
                setRecords((response.data as any).results)
                setTotalCount((response.data as any).count)

                // Calculate total commission
                const total = (response.data as any)?.results.reduce((sum: number, record: CommissionRecord) =>
                    sum + Number(record.commission_amount), 0
                )
                setTotalCommission(total)
            } else {
                setError('Failed to fetch commission records')
            }
        } catch (err) {
            setError('An error occurred while fetching commission records')
            console.error('Error fetching commission records:', err)
        } finally {
            setLoading(false)
        }
    }

    const filteredRecords = records.filter(record =>
        record.merchant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.transaction_reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.id.toString().includes(searchTerm)
    )

    const totalPages = Math.ceil(totalCount / pageSize)

    if (loading && records.length === 0) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-orange-500" />
                    <p className="text-gray-300">Loading commission records...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-900">
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div>
                                <h1 className="text-3xl font-bold text-white">Commission Records</h1>
                                <p className="text-gray-300 mt-1">View and analyze commission tracking data</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Summary Card */}
                <div className="border border-white/20 bg-slate-900/95 backdrop-blur-md shadow-sm rounded-lg p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className="p-3 bg-orange-500/20 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                                <Calculator className="h-6 w-6 text-orange-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-white">{totalCount}</h3>
                            <p className="text-sm text-gray-300">Total Records</p>
                        </div>
                        <div className="text-center">
                            <div className="p-3 bg-green-500/20 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                                <DollarSign className="h-6 w-6 text-green-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-white">${totalCommission.toFixed(2)}</h3>
                            <p className="text-sm text-gray-300">Total Commission</p>
                        </div>
                        <div className="text-center">
                            <div className="p-3 bg-blue-500/20 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                                <TrendingUp className="h-6 w-6 text-blue-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-white">
                                {records.length > 0 ? (totalCommission / records.length).toFixed(2) : '0.00'}%
                            </h3>
                            <p className="text-sm text-gray-300">Average Rate</p>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="border border-white/20 bg-slate-900/95 backdrop-blur-md shadow-sm rounded-lg p-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    placeholder="Search by merchant, transaction..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 bg-slate-800 border-white/20 text-white"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Merchant</label>
                            <select
                                value={merchantFilter}
                                onChange={(e) => setMerchantFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-white/20 bg-slate-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                <option value="">All Merchants</option>
                                {/* This would be populated with actual merchants from the API */}
                                <option value="1">Merchant 1</option>
                                <option value="2">Merchant 2</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Currency</label>
                            <select
                                value={currencyFilter}
                                onChange={(e) => setCurrencyFilter(e.target.value)}
                                className="w-full px-3 py-2 border border-white/20 bg-slate-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                <option value="">All Currencies</option>
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                                <option value="CAD">CAD</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <Button
                                onClick={() => {
                                    setMerchantFilter('')
                                    setCurrencyFilter('')
                                    setSearchTerm('')
                                }}
                                variant="outline"
                                className="w-full text-gray-300 border-gray-600 hover:bg-gray-800 hover:text-white"
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-6">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                            <p className="text-red-300">{error}</p>
                        </div>
                    </div>
                )}

                {/* Records Table */}
                <div className="border border-white/20 bg-slate-900/95 backdrop-blur-md shadow-sm rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-white/20">
                            <thead className="bg-gray-800/50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Transaction
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Merchant
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Commission Rate
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Commission Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Currency
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Created
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-slate-800/30 divide-y divide-white/20">
                                {filteredRecords.map((record) => (
                                    <tr key={record.id} className="hover:bg-white/5">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="text-sm font-medium text-white">
                                                    #{record.transaction}
                                                </div>
                                                <div className="ml-2 text-xs text-gray-400 font-mono bg-gray-700/50 px-2 py-1 rounded">
                                                    {record.transaction_reference}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="p-1 bg-orange-500/20 rounded mr-2">
                                                    <Building className="h-4 w-4 text-orange-400" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-medium text-white">{record.merchant_name}</div>
                                                    {record.submerchant_name && (
                                                        <div className="text-sm text-gray-400">{record.submerchant_name}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                            <div className="flex items-center">
                                                <div className="p-1 bg-green-500/20 rounded mr-1">
                                                    <DollarSign className="h-4 w-4 text-green-400" />
                                                </div>
                                                ${Number(record.amount_fiat).toFixed(2)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                {Number(record.commission_rate).toFixed(2)}%
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                                            <div className="flex items-center">
                                                <div className="p-1 bg-blue-500/20 rounded mr-1">
                                                    <DollarSign className="h-4 w-4 text-blue-400" />
                                                </div>
                                                ${Number(record.commission_amount).toFixed(2)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                            {record.currency}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                            <div className="flex items-center">
                                                <div className="p-1 bg-purple-500/20 rounded mr-1">
                                                    <Calendar className="h-4 w-4 text-purple-400" />
                                                </div>
                                                {safeFormatDistanceToNow(record.created_at, 'Unknown', { addSuffix: true })}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {filteredRecords.length === 0 && !loading && (
                        <div className="text-center py-12">
                            <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-white mb-2">No commission records found</h3>
                            <p className="text-gray-400 mb-4">
                                {searchTerm || merchantFilter || currencyFilter
                                    ? 'Try adjusting your filters to see more results.'
                                    : 'Commission records will appear here as transactions are processed.'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6">
                        <div className="text-sm text-gray-300">
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="text-gray-300 border-gray-600 hover:bg-gray-800 hover:text-white"
                            >
                                Previous
                            </Button>
                            <span className="text-sm text-gray-300">
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="text-gray-300 border-gray-600 hover:bg-gray-800 hover:text-white"
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
